/* INVENTORY REPORT (2026-10-23) — the screen over backend/inventoryReport.js: Max's weekly S&OP inventory week as a job.
   One Friday at a time: on hand and amount (USD at buying cost) per entity by the age of each stock layer, the top 30 SKUs
   per entity, what is on its way, and every layer behind the figures — the working file's two pivots, with the loads that
   fill them (NetSuite and SAP read directly, the HJPN count and the EUSU list uploaded) and the classification its filters
   read. Project Approval's classes, like the Sales Data Load. Admin only (`inventory_report`). */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileUp, Play, RefreshCw, Check, ChevronDown } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import Header from './Header';
import TabIcon from './TabIcon';
import ModuleHelp from './ModuleHelp';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './InventoryReport.css';

const API = `${BACKEND_URL}/api/inventory`;
const ENTITIES = ['HUSA', 'HEMA', 'HPAC', 'HINC', 'HJPN', 'EUSU'];
const BUCKETS = ['Under 90 days', 'Over 90 days', 'Over 180 days', 'Over 1 year'];
const BKEY = { 'Under 90 days': 'u90', 'Over 90 days': 'o90', 'Over 180 days': 'o180', 'Over 1 year': 'o1y' };
/* AGE IS AN ORDER, SO ONE HUE: the validated blue ramp (dataviz, --ordinal), older stock darker on light and brighter on
   dark — the bucket somebody has to act on is the one that stands out in both themes. */
const RAMP = { light: ['#86b6ef', '#3987e5', '#1c5cab', '#0d366b'], dark: ['#184f95', '#3987e5', '#86b6ef', '#cde2fb'] };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (v) => { if (!v) return ''; const s = String(v).slice(0, 10); const [y, m, d] = s.split('-'); return y && m && d ? `${d}/${MONTHS[Number(m) - 1]}/${y}` : s; };
const fmtWhen = (v) => { if (!v) return ''; const d = new Date(v); if (Number.isNaN(d.getTime())) return String(v); const vn = new Date(d.getTime() + 7 * 3600 * 1000); return `${fmtDay(vn.toISOString())} ${vn.toISOString().slice(11, 16)}`; };
const n0 = (v) => (v === null || v === undefined || v === '' ? '' : Math.round(Number(v)).toLocaleString('en-US'));
const usd = (v) => (v === null || v === undefined ? '' : `${Number(v) < 0 ? '−' : ''}US$ ${Math.round(Math.abs(Number(v))).toLocaleString('en-US')}`);
const n2 = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const weekdayOf = (v) => DOW[new Date(`${String(v).slice(0, 10)}T00:00:00Z`).getUTCDay()] || '';
const isFriday = (v) => new Date(`${String(v).slice(0, 10)}T00:00:00Z`).getUTCDay() === 5;
const pct = (a, b) => (b ? `${((a / b) * 100).toFixed(1)}%` : '');
const call = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
};
const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark';
function FilterLine({ t, tools, shown, total }) {
  if (!tools.active) return null;
  return <div className="iv-filterline">{shown !== total && t('xInventory.nOfN', { n: n0(shown), total: n0(total) })} <button type="button" className="pa-btn small" onClick={tools.clear}>{t('xInventory.clearFilters')}</button></div>;
}

/* ---------------------------------------------------------------- dashboard */
/* ---------------------------------------------------------------- a filter that takes several values
   Max 30/10 (the Dashboard): "thêm filter SKU trên này luôn và có thể multichoice" — the SKU picker and the five class
   filters are one control: a button that says what is picked, a list with a search box and ticks. Every word typed must
   match (SKU#, product or colour for a SKU). The server already read several values per filter ("a|b"). */
export function MultiPick({ t, label, options, value, onChange, testId, searchPh }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [open]);
  const sel = useMemo(() => new Set(value), [value]);
  const list = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = options.filter((o) => !words.length || words.every((w) => `${o.v} ${o.sub || ''}`.toLowerCase().includes(w)));
    // what is ticked stays on top, so it can be unticked without scrolling 2,788 SKUs
    return [...hits.filter((o) => sel.has(o.v)), ...hits.filter((o) => !sel.has(o.v))];
  }, [options, q, sel]);
  const shown = list.slice(0, 200);
  const toggle = (v) => onChange(sel.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const text = !value.length ? t('xInventory.all') : value.length === 1 ? value[0] : t('xInventory.mpN', { n: value.length });
  return (
    <div className="iv-field iv-mp" ref={ref}>
      <span>{label}</span>
      <button type="button" className={`iv-mp-btn${value.length ? ' on' : ''}`} onClick={() => { setOpen((o) => !o); setQ(''); }} title={value.join(', ')} data-iv-mp={testId}>
        <span>{text}</span><ChevronDown size={13} />
      </button>
      {open && (
        <div className="iv-mp-pop" data-iv-mppop={testId}>
          <input autoFocus className="iv-mp-q" value={q} placeholder={searchPh || t('xInventory.mpSearch')} onChange={(e) => setQ(e.target.value)} data-iv-mpq={testId} />
          <div className="iv-mp-list">
            {shown.map((o) => (
              <label key={o.v} className={`iv-mp-row${sel.has(o.v) ? ' on' : ''}`} data-iv-mprow={o.v}>
                <input type="checkbox" checked={sel.has(o.v)} onChange={() => toggle(o.v)} />
                <span className="iv-mp-v">{o.v}</span>{o.sub && <span className="iv-mp-sub" title={`${o.v} - ${o.sub}`}>- {o.sub}</span>}
              </label>
            ))}
            {!list.length && <div className="iv-mp-more">{t('xInventory.nothingHere')}</div>}
            {list.length > shown.length && <div className="iv-mp-more">{t('xInventory.mpMore', { n: list.length - shown.length })}</div>}
          </div>
          <div className="iv-mp-foot">
            <span>{t('xInventory.mpN', { n: value.length })}</span>
            {q.trim() && list.length <= 200 && list.some((o) => !sel.has(o.v)) && (
              <button type="button" className="pa-btn small" onClick={() => onChange([...new Set([...value, ...list.map((o) => o.v)])])}>{t('xInventory.mpAll', { n: list.length })}</button>
            )}
            {value.length > 0 && <button type="button" className="pa-btn small" onClick={() => onChange([])} data-iv-mpclear={testId}>{t('xInventory.mpClear')}</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ t, friday, flash }) {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [topEntity, setTopEntity] = useState('HUSA');
  const [opts, setOpts] = useState(null);         // the pickers' lists — kept while a new filter reloads the figures
  useEffect(() => { if (data?.options) setOpts(data.options); }, [data]);
  // each suggestion reads "SKU - SAP - name - colour" (Max 30/10), and every part of it can be typed
  const skuOpts = useMemo(() => (opts?.sku || []).map((o) => ({ v: o.v, sub: [o.s, o.p, o.c].filter(Boolean).join(' - ') })), [opts]);
  useEffect(() => {
    let live = true;
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v && v.length).map(([k, v]) => [k, v.join('|')])).toString();
    setData(null);
    call('GET', `/week/${friday}/summary${qs ? `?${qs}` : ''}`).then((d) => { if (live) setData(d); }).catch((e) => flash(e.message, true));
    return () => { live = false; };
  }, [friday, filters, flash]);
  const grid = useMemo(() => {
    const by = {};
    (data?.grid || []).forEach((g) => { by[g.entity] = by[g.entity] || {}; by[g.entity][g.bucket || ''] = g; });
    const prev = Object.fromEntries((data?.prevTotals || []).map((p) => [p.entity, p]));
    const rows = ENTITIES.map((e) => {
      const b = by[e] || {};
      const cells = BUCKETS.map((k) => ({ qty: Number(b[k]?.qty || 0), amt: Number(b[k]?.amt || 0) }));
      const blank = b[''] ? { qty: Number(b[''].qty), amt: Number(b[''].amt) } : null;
      const qty = cells.reduce((s, c) => s + c.qty, 0) + (blank?.qty || 0);
      const amt = cells.reduce((s, c) => s + c.amt, 0) + (blank?.amt || 0);
      return { entity: e, cells, qty, amt, blank, prevAmt: prev[e] ? Number(prev[e].amt) : null, prevQty: prev[e] ? Number(prev[e].qty) : null, loaded: data?.loaded ? data.loaded.includes(e) : !!by[e] };
    });
    const tot = { cells: BUCKETS.map((_, i) => ({ qty: rows.reduce((s, r) => s + r.cells[i].qty, 0), amt: rows.reduce((s, r) => s + r.cells[i].amt, 0) })), qty: rows.reduce((s, r) => s + r.qty, 0), amt: rows.reduce((s, r) => s + r.amt, 0),
      /* the change is counted over the entities loaded on BOTH days — an entity not uploaded yet on one side is not a
         drop or a rise of its whole value (review 23/10) */
      prevAmt: rows.some((r) => r.loaded && r.prevAmt !== null) ? rows.filter((r) => r.loaded && r.prevAmt !== null).reduce((s, r) => s + r.prevAmt, 0) : null,
      bothAmt: rows.filter((r) => r.loaded && r.prevAmt !== null).reduce((s, r) => s + r.amt, 0) };
    return { rows, tot };
  }, [data]);
  const transit = useMemo(() => {
    const m = {}; (data?.transit || []).forEach((x) => { m[x.entity] = m[x.entity] || { po: 0, to: 0, amt: 0 }; m[x.entity][x.kind] += Number(x.qty); m[x.entity].amt += Number(x.amt); });
    return m;
  }, [data]);
  const ramp = isDark() ? RAMP.dark : RAMP.light;
  const chartRows = grid.rows.filter((r) => r.loaded).map((r) => ({ entity: r.entity, ...Object.fromEntries(BUCKETS.map((b, i) => [BKEY[b], Math.round(r.cells[i].amt)])) }));
  const top = (data?.top || []).filter((x) => x.entity === topEntity);
  const topTotal = grid.rows.find((r) => r.entity === topEntity)?.amt || 0;
  const topSum = top.reduce((s, x) => s + Number(x.amt), 0);
  const transitQty = Object.values(transit).reduce((s, x) => s + x.po + x.to, 0);
  const transitAmt = Object.values(transit).reduce((s, x) => s + x.amt, 0);
  if (data?.empty) return <p className="iv-empty">{t('xInventory.nothingLoaded')}</p>;
  return (
    <div className="pa-section" data-tour="iv-dash">
      <div className="pa-toolbar iv-filters">
        <MultiPick t={t} label={t('xInventory.sku')} options={skuOpts} value={filters.sku || []} onChange={(v) => setFilters((f) => ({ ...f, sku: v }))} testId="sku" searchPh={t('xInventory.mpSearchSku')} />
        {['type1', 'type2', 'type3', 'type4', 'type5'].map((k) => (
          <MultiPick key={k} t={t} label={t(`xInventory.${k}`)} options={(opts?.[k] || []).map((v) => ({ v }))} value={filters[k] || []}
            onChange={(v) => setFilters((f) => ({ ...f, [k]: v }))} testId={k} />
        ))}
        {Object.values(filters).some((v) => v && v.length) && <button type="button" className="pa-btn small" onClick={() => setFilters({})}>{t('xInventory.clearFilters')}</button>}
      </div>
      {!data ? <p className="iv-hint">{t('xInventory.loading')}</p> : (
        <>
          <div className="iv-tiles">
            <div className="iv-tile"><span>{t('xInventory.onHand')}</span><b>{n0(grid.tot.qty)}</b><em>{t('xInventory.pcs')}</em></div>
            <div className="iv-tile"><span>{t('xInventory.amount')}</span><b>{usd(grid.tot.amt)}</b><em>{grid.tot.prevAmt !== null ? t('xInventory.vsPrev', { week: `${data.prev?.week_code || ''} ${fmtDay(data.prev?.friday)}`.trim(), d: usd(grid.tot.bothAmt - grid.tot.prevAmt) }) : t('xInventory.atCost')}</em></div>
            <div className="iv-tile"><span>{t('xInventory.over1y')}</span><b>{pct(grid.tot.cells[3].amt, grid.tot.amt)}</b><em>{usd(grid.tot.cells[3].amt)}</em></div>
            <div className="iv-tile"><span>{t('xInventory.inTransit')}</span><b>{n0(transitQty)}</b><em>{usd(transitAmt)}</em></div>
          </div>
          {data.unclassified > 0 && <p className="iv-hint">{t('xInventory.unclassifiedNote', { n: n0(data.unclassified) })}</p>}
          <h3 className="iv-h">{t('xInventory.byAge')}</h3>
          <div className="iv-wrap">
            <table className="pa-table iv-grid">
              <thead>
                <tr><th rowSpan={2}>{t('xInventory.entity')}</th>{BUCKETS.map((b) => <th key={b} colSpan={2}>{t(`xInventory.${BKEY[b]}`)}</th>)}<th colSpan={2}>{t('xInventory.total')}</th><th rowSpan={2}>{t('xInventory.vsLastWeek')}</th></tr>
                <tr>{[...BUCKETS, 'total'].map((b) => <React.Fragment key={b}><th className="num">{t('xInventory.qty')}</th><th className="num">{t('xInventory.amountUsd')}</th></React.Fragment>)}</tr>
              </thead>
              <tbody>
                {grid.rows.map((r) => (
                  <tr key={r.entity} className={r.loaded ? '' : 'iv-dim'}>
                    <td><b>{r.entity}</b>{!r.loaded && <span className="iv-sub"> · {t('xInventory.notLoaded')}</span>}{r.blank && <span className="iv-sub" title={t('xInventory.noAgeHint')}> · {t('xInventory.noAge', { n: n0(r.blank.qty) })}</span>}</td>
                    {r.cells.map((c, i) => <React.Fragment key={i}><td className="num">{n0(c.qty)}</td><td className="num">{n0(c.amt)}</td></React.Fragment>)}
                    <td className="num"><b>{n0(r.qty)}</b></td><td className="num"><b>{n0(r.amt)}</b></td>
                    <td className={`num ${r.prevAmt !== null && r.amt - r.prevAmt < 0 ? 'iv-neg' : ''}`}>{r.prevAmt !== null ? `${r.amt - r.prevAmt >= 0 ? '+' : '−'}${n0(Math.abs(r.amt - r.prevAmt))}` : ''}</td>
                  </tr>
                ))}
                <tr className="iv-total"><td>{t('xInventory.total')}</td>{grid.tot.cells.map((c, i) => <React.Fragment key={i}><td className="num">{n0(c.qty)}</td><td className="num">{n0(c.amt)}</td></React.Fragment>)}<td className="num">{n0(grid.tot.qty)}</td><td className="num">{n0(grid.tot.amt)}</td>
                  <td className="num">{grid.tot.prevAmt !== null ? `${grid.tot.amt - grid.tot.prevAmt >= 0 ? '+' : '−'}${n0(Math.abs(grid.tot.amt - grid.tot.prevAmt))}` : ''}</td></tr>
                <tr className="iv-pct"><td>{t('xInventory.agingPct')}</td>{grid.tot.cells.map((c, i) => <React.Fragment key={i}><td /><td className="num">{pct(c.amt, grid.tot.amt)}</td></React.Fragment>)}<td /><td className="num">100%</td><td /></tr>
              </tbody>
            </table>
          </div>
          <div className="iv-chart" aria-label={t('xInventory.byAge')}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartRows} margin={{ top: 8, right: 16, bottom: 4, left: 16 }}>
                <CartesianGrid vertical={false} stroke={isDark() ? '#2c2c2a' : '#e8e8e4'} />
                <XAxis dataKey="entity" tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1000).toLocaleString('en-US')}k`} tickLine={false} axisLine={false} width={64} />
                <Tooltip formatter={(v, name) => [usd(v), t(`xInventory.${name}`)]} cursor={{ fill: 'rgba(120,120,120,.08)' }} />
                <Legend formatter={(name) => t(`xInventory.${name}`)} itemSorter={null} />
                {BUCKETS.map((b, i) => <Bar key={b} dataKey={BKEY[b]} stackId="a" fill={ramp[i]} stroke={isDark() ? '#1a1a19' : '#fcfcfb'} strokeWidth={1} maxBarSize={64} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <h3 className="iv-h">{t('xInventory.top30')}</h3>
          <div className="pa-tabs iv-subtabs">
            {ENTITIES.map((e) => <button key={e} type="button" className={`pa-tab${topEntity === e ? ' active' : ''}`} onClick={() => setTopEntity(e)}>{e}</button>)}
          </div>
          <p className="iv-hint">{t('xInventory.topShare', { amt: usd(topSum), share: pct(topSum, topTotal), entity: topEntity })}</p>
          <div className="iv-wrap">
            <table className="pa-table">
              <thead><tr><th>#</th><th>{t('xInventory.sku')}</th><th>{t('xInventory.sap')}</th><th>{t('xInventory.product')}</th><th>{t('xInventory.color')}</th><th className="num">{t('xInventory.qty')}</th><th className="num">{t('xInventory.amountUsd')}</th><th className="num">{t('xInventory.share')}</th></tr></thead>
              <tbody>
                {top.map((x) => <tr key={x.sku}><td>{x.rk}</td><td className="iv-code">{x.sku}</td><td className="iv-code">{x.sap}</td><td>{x.product}</td><td>{x.color}</td><td className="num">{n0(x.qty)}</td><td className="num">{n0(x.amt)}</td><td className="num">{pct(Number(x.amt), topTotal)}</td></tr>)}
                {!top.length && <tr><td colSpan={8} className="iv-empty">{t('xInventory.nothingHere')}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- loads */
const SOURCES = [
  { key: 'netsuite', entities: ['HUSA', 'HEMA', 'HPAC'], label: 'srcNetsuite', action: 'netsuite' },
  { key: 'transit', entities: ['TRANSIT'], label: 'srcTransit', action: 'transit' },
  { key: 'hinc', entities: ['HINC'], label: 'srcHinc', action: 'hinc' },
  { key: 'hjpn', entities: ['HJPN'], label: 'srcHjpn', upload: 'HJPN' },
  { key: 'eusu', entities: ['EUSU'], label: 'srcEusu', upload: 'EUSU' },
];
function Loads({ t, week, friday, edit, running, reload, flash }) {
  const [busy, setBusy] = useState(false);
  const loads = Object.fromEntries((week?.loads || []).map((l) => [l.entity, l]));
  const start = async (kind) => {
    setBusy(true);
    try { await call('POST', `/week/${friday}/load`, { kind }); flash(t('xInventory.loadStarted')); await reload(); }
    catch (e) { flash(`${t('xInventory.error')}: ${e.message}`, true); } finally { setBusy(false); }
  };
  const upload = async (entity, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const data = await new Promise((ok, no) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = no; r.readAsDataURL(file); });
      const out = await call('POST', `/week/${friday}/upload/${entity}`, { fileName: file.name, data });
      flash(`${entity}: ${t('xInventory.uploaded', { n: n0(out.rows_n), qty: n0(out.qty) })}${out.warning ? ` — ${out.warning}` : ''}`, !!out.warning);
      await reload();
    } catch (e) { flash(`${t('xInventory.error')}: ${e.message}`, true); } finally { setBusy(false); }
  };
  return (
    <div className="pa-section" data-tour="iv-loads">
      <p className="iv-hint">{t('xInventory.loadsHint')}</p>
      <div className="iv-wrap">
        <table className="pa-table iv-loads">
          <thead><tr><th>{t('xInventory.source')}</th><th>{t('xInventory.entity')}</th><th>{t('xInventory.status')}</th><th className="num">{t('xInventory.rows')}</th><th className="num">{t('xInventory.qty')}</th><th className="num">{t('xInventory.amountUsd')}</th><th className="num">{t('xInventory.excluded')}</th><th>{t('xInventory.by')}</th><th className="iv-col-action">{t('xInventory.action')}</th></tr></thead>
          <tbody>
            {SOURCES.map((s) => s.entities.map((e, i) => {
              const l = loads[e];
              /* the NetSuite load reads in transit too, so Transit waits for it as well (review 23/10) */
              const isRun = running.includes(s.action || '') || (s.action === 'transit' && running.includes('netsuite')) || l?.status === 'running';
              return (
                <tr key={e}>
                  {i === 0 && <td rowSpan={s.entities.length}><b>{t(`xInventory.${s.label}`)}</b></td>}
                  <td>{e === 'TRANSIT' ? t('xInventory.inTransit') : e}</td>
                  <td>{l ? <span className={`iv-pill st-${l.status}${l.source === 'legacy' ? ' st-legacy' : ''}`} title={l.error_text || ''}>{l.source === 'legacy' ? t('xInventory.st_legacy') : t(`xInventory.st_${l.status}`)}</span> : <span className="iv-pill st-none">{t('xInventory.st_none')}</span>}</td>
                  <td className="num">{n0(l?.rows_n)}</td><td className="num">{n0(l?.qty)}</td><td className="num">{n0(l?.amount_usd)}</td>
                  <td className="num" title={t('xInventory.excludedHint')}>{l?.excluded_n ? `${n0(l.excluded_n)} / ${n0(l.excluded_qty)}` : ''}</td>
                  <td className="iv-sub">{l ? `${l.started_by || ''} · ${fmtWhen(l.finished_at || l.started_at)}${l.file_name ? ` · ${l.file_name}` : ''}` : ''}{l?.error_text && <div className="iv-err">{l.error_text}</div>}</td>
                  {i === 0 && <td rowSpan={s.entities.length} className="iv-col-action">
                    {edit && s.action && <button type="button" className="pa-btn small primary" disabled={busy || isRun} onClick={() => start(s.action)}><Play size={13} /> {isRun ? t('xInventory.running') : t(s.action === 'netsuite' ? 'xInventory.loadNetsuite' : s.action === 'hinc' ? 'xInventory.loadSap' : 'xInventory.loadTransit')}</button>}
                    {edit && s.upload && <label className={`pa-btn small primary${busy ? ' disabled' : ''}`}><FileUp size={13} /> {t('xInventory.upload')}<input type="file" accept=".xlsx,.xlsm" hidden disabled={busy} onChange={(ev) => { upload(s.upload, ev.target.files[0]); ev.target.value = ''; }} /></label>}
                  </td>}
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>
      <p className="iv-hint">{t('xInventory.rulesHint')}</p>
    </div>
  );
}

/* ---------------------------------------------------------------- layers */
function Layers({ t, friday, flash }) {
  const [entity, setEntity] = useState('HUSA');
  const [rows, setRows] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  useEffect(() => {
    let live = true; setRows(null);
    call('GET', `/week/${friday}/layers?entity=${entity}`).then((d) => { if (live) setRows(d.rows); }).catch((e) => { if (live) flash(e.message, true); });
    return () => { live = false; };
  }, [friday, entity, flash]);
  const list = useMemo(() => (rows || []).filter((r) => showExcluded || !r.excluded), [rows, showExcluded]);
  const cols = useMemo(() => [
    { key: 'ivSku', get: (r) => r.sku }, { key: 'ivSap', get: (r) => r.sap || '' }, { key: 'ivProduct', get: (r) => r.product || '' }, { key: 'ivColor', get: (r) => r.color || '' },
    { key: 'ivInbound', get: (r) => r.inbound_date || '' }, { key: 'ivPo', get: (r) => r.po || '' }, { key: 'ivQty', get: (r) => Number(r.qty) },
    { key: 'ivCost', get: (r) => (r.unit_cost_usd === null ? '' : Number(r.unit_cost_usd)) }, { key: 'ivAmt', get: (r) => Number(r.amount_usd || 0) },
    { key: 'ivAge', get: (r) => (r.age_days === null ? '' : r.age_days) }, { key: 'ivBucket', get: (r) => (r.bucket ? t(`xInventory.${BKEY[r.bucket]}`) : '') },
    { key: 'ivType1', get: (r) => r.type1 || t('xInventory.unclassified') }, { key: 'ivType3', get: (r) => r.type3 || '' }, { key: 'ivType5', get: (r) => r.type5 || '' },
    { key: 'ivWhy', get: (r) => r.exclude_reason || '' },
  ], [t]);
  const tools = useColumnTools(list, cols);
  const H = (k, label, cls) => <SortTh label={label} className={cls} {...tools.thProps(k)} />;
  return (
    <div className="pa-section" data-tour="iv-layers">
      <div className="pa-toolbar iv-filters">
        <div className="pa-tabs iv-subtabs">{ENTITIES.map((e) => <button key={e} type="button" className={`pa-tab${entity === e ? ' active' : ''}`} onClick={() => setEntity(e)}>{e}</button>)}</div>
        <label className="iv-check"><input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} /> {t('xInventory.showExcluded')}</label>
        <span className="iv-hint">{rows ? t('xInventory.layersCount', { n: n0(list.length), qty: n0(list.reduce((s, r) => s + (r.excluded ? 0 : Number(r.qty)), 0)) }) : t('xInventory.loading')}</span>
      </div>
      <FilterLine t={t} tools={tools} shown={tools.shown.length} total={list.length} />
      <div className="iv-freeze">
        <table className="pa-table iv-layers">
          <thead><tr>{H('ivSku', t('xInventory.sku'))}{H('ivSap', t('xInventory.sap'))}{H('ivProduct', t('xInventory.product'))}{H('ivColor', t('xInventory.color'))}{H('ivInbound', t('xInventory.inbound'))}{H('ivPo', 'PO')}
            {H('ivQty', t('xInventory.qty'), 'num')}{H('ivCost', t('xInventory.unitCost'), 'num')}{H('ivAmt', t('xInventory.amountUsd'), 'num')}{H('ivAge', t('xInventory.age'), 'num')}{H('ivBucket', t('xInventory.bucket'))}
            {H('ivType1', t('xInventory.type1'))}{H('ivType3', t('xInventory.type3'))}{H('ivType5', t('xInventory.type5'))}{showExcluded && H('ivWhy', t('xInventory.excludedWhy'))}</tr></thead>
          <tbody>
            {tools.shown.slice(0, 3000).map((r) => (
              <tr key={r.layer_pk} className={r.excluded ? 'iv-dim' : ''}>
                <td className="iv-code">{r.sku}</td><td className="iv-code">{r.sap}</td><td>{r.product}</td><td>{r.color}</td>
                <td className="iv-code" title={r.inbound_default ? t('xInventory.inboundDefault') : ''}>{fmtDay(r.inbound_date)}{r.inbound_default ? ' *' : ''}</td>
                <td className="iv-po">{r.po}</td><td className="num">{n0(r.qty)}</td><td className="num">{n2(r.unit_cost_usd)}</td><td className="num">{n0(r.amount_usd)}</td><td className="num">{r.age_days}</td>
                <td>{r.bucket ? t(`xInventory.${BKEY[r.bucket]}`) : ''}</td><td>{r.type1 || <span className="iv-sub">{t('xInventory.unclassified')}</span>}</td><td>{r.type3}</td><td>{r.type5}</td>
                {showExcluded && <td className="iv-sub">{r.exclude_reason}</td>}
              </tr>
            ))}
            {rows && !tools.shown.length && <tr><td colSpan={15} className="iv-empty">{t('xInventory.nothingHere')}</td></tr>}
          </tbody>
        </table>
      </div>
      {tools.shown.length > 3000 && <p className="iv-hint">{t('xInventory.first3000', { n: n0(tools.shown.length) })}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- in transit */
function Transit({ t, friday, flash }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true; setData(null);
    call('GET', `/week/${friday}/transit`).then((d) => { if (live) setData(d); }).catch((e) => { if (live) flash(e.message, true); });
    return () => { live = false; };
  }, [friday, flash]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => !r.excluded), [data]);
  const cols = useMemo(() => [
    { key: 'ivEntity', get: (r) => r.entity }, { key: 'ivKind', get: (r) => t(`xInventory.kind_${r.kind}`) }, { key: 'ivPo', get: (r) => r.po || '' }, { key: 'ivSku', get: (r) => r.sku || '' },
    { key: 'ivProduct', get: (r) => r.product || '' }, { key: 'ivColor', get: (r) => r.color || '' }, { key: 'ivLoc', get: (r) => r.location || '' },
    { key: 'ivShipped', get: (r) => Number(r.qty_shipped || 0) }, { key: 'ivReceived', get: (r) => Number(r.qty_received || 0) }, { key: 'ivOpen', get: (r) => Number(r.qty_open) },
    { key: 'ivAmt', get: (r) => Number(r.amount_usd || 0) }, { key: 'ivShip', get: (r) => r.ship_date || '' }, { key: 'ivExpected', get: (r) => r.expected_date || '' },
  ], [t]);
  const tools = useColumnTools(rows, cols);
  const H = (k, label, cls) => <SortTh label={label} className={cls} {...tools.thProps(k)} />;
  return (
    <div className="pa-section" data-tour="iv-transit">
      <p className="iv-hint">{data ? t('xInventory.transitHint', { day: fmtDay(data.readOn) }) : t('xInventory.loading')}</p>
      <FilterLine t={t} tools={tools} shown={tools.shown.length} total={rows.length} />
      <div className="iv-freeze">
        <table className="pa-table">
          <thead><tr>{H('ivEntity', t('xInventory.entity'))}{H('ivKind', t('xInventory.kind'))}{H('ivPo', 'PO')}{H('ivSku', t('xInventory.sku'))}{H('ivProduct', t('xInventory.product'))}{H('ivColor', t('xInventory.color'))}{H('ivLoc', t('xInventory.location'))}
            {H('ivShipped', t('xInventory.shipped'), 'num')}{H('ivReceived', t('xInventory.received'), 'num')}{H('ivOpen', t('xInventory.onTheWay'), 'num')}{H('ivAmt', t('xInventory.amountUsd'), 'num')}{H('ivShip', t('xInventory.shipDate'))}{H('ivExpected', t('xInventory.expected'))}</tr></thead>
          <tbody>
            {tools.shown.map((r) => (
              <tr key={r.transit_pk}><td>{r.entity}</td><td>{t(`xInventory.kind_${r.kind}`)}</td><td className="iv-code">{r.po}</td><td className="iv-code">{r.sku}</td><td>{r.product}</td><td>{r.color}</td><td className="iv-sub">{r.location}</td>
                <td className="num">{n0(r.qty_shipped)}</td><td className="num">{n0(r.qty_received)}</td><td className="num"><b>{n0(r.qty_open)}</b></td><td className="num">{n0(r.amount_usd)}</td><td className="iv-code">{fmtDay(r.ship_date)}</td><td className="iv-code">{fmtDay(r.expected_date)}</td></tr>
            ))}
            {data && !tools.shown.length && <tr><td colSpan={13} className="iv-empty">{t('xInventory.nothingHere')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- classification */
const T7 = ['type1', 'type2', 'type3', 'type4', 'type5', 'type6', 'type7'];
function Classification({ t, friday, edit, flash }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const asked = useRef(0);
  const load = useCallback(() => { const n = ++asked.current; return call('GET', `/classification?friday=${friday}`).then((d) => { if (n === asked.current) setData(d); }).catch((e) => flash(e.message, true)); }, [friday, flash]);
  useEffect(() => { setData(null); setDraft({}); load(); }, [load]);
  const valueOf = (m, k) => (draft[m.sku]?.[k] !== undefined ? draft[m.sku][k] : (m.suggestion?.[k] || ''));
  const save = async (m) => {
    setBusy(true);
    try {
      await call('PUT', `/classification/${encodeURIComponent(m.sku)}`, { sap: m.sap, product: m.product, color: m.color, ...Object.fromEntries(T7.map((k) => [k, valueOf(m, k)])) });
      flash(t('xInventory.classSaved', { sku: m.sku })); setDraft((d) => { const n = { ...d }; delete n[m.sku]; return n; }); await load();
    } catch (e) { flash(`${t('xInventory.error')}: ${e.message}`, true); } finally { setBusy(false); }
  };
  const level1 = (data?.missing || []).filter((m) => m.suggestion?.level === 1);
  const acceptAll = async () => {
    setBusy(true);
    try {
      const out = await call('POST', '/classification/bulk', { source: 'suggested', rows: level1.map((m) => ({ sku: m.sku, sap: m.sap, product: m.product, color: m.color, ...Object.fromEntries(T7.map((k) => [k, valueOf(m, k)])) })) });
      flash(t('xInventory.accepted', { n: n0(out.added) })); await load();
    } catch (e) { flash(`${t('xInventory.error')}: ${e.message}`, true); } finally { setBusy(false); }
  };
  return (
    <div className="pa-section" data-tour="iv-class">
      <p className="iv-hint">{data ? t('xInventory.classHint', { n: n0(data.missing.length), count: n0(data.count), l1: n0(level1.length) }) : t('xInventory.loading')}</p>
      {edit && level1.length > 0 && <div className="pa-toolbar"><button type="button" className="pa-btn primary" disabled={busy} onClick={acceptAll}><Check size={14} /> {t('xInventory.acceptAll', { n: n0(level1.length) })}</button></div>}
      {T7.map((k) => <datalist key={k} id={`iv-dl-${k}`}>{(data?.values?.[k] || []).map((v) => <option key={v} value={v} />)}</datalist>)}
      <div className="iv-freeze">
        <table className="pa-table iv-class">
          <thead><tr><th>{t('xInventory.sku')}</th><th>{t('xInventory.product')}</th><th>{t('xInventory.entities')}</th><th className="num">{t('xInventory.amountUsd')}</th>{T7.map((k) => <th key={k}>{t(`xInventory.${k}`)}</th>)}<th>{t('xInventory.suggested')}</th><th className="iv-col-action">{t('xInventory.action')}</th></tr></thead>
          <tbody>
            {(data?.missing || []).slice(0, 400).map((m) => (
              <tr key={m.sku}>
                <td className="iv-code">{m.sku}<div className="iv-sub">{m.sap}</div></td><td>{m.product}<div className="iv-sub">{m.color}</div></td><td>{m.entities}</td><td className="num">{n0(m.amt)}</td>
                {T7.map((k) => <td key={k}><input list={`iv-dl-${k}`} value={valueOf(m, k)} disabled={!edit} onChange={(e) => setDraft((d) => ({ ...d, [m.sku]: { ...(d[m.sku] || {}), [k]: e.target.value } }))} /></td>)}
                <td className="iv-sub">{m.suggestion ? t(m.suggestion.level === 1 ? 'xInventory.sugL1' : 'xInventory.sugL2', { share: m.suggestion.share }) : t('xInventory.sugNone')}</td>
                <td className="iv-col-action">{edit && <button type="button" className="pa-btn small primary" disabled={busy || !valueOf(m, 'type1')} onClick={() => save(m)}>{t('xInventory.save')}</button>}</td>
              </tr>
            ))}
            {data && !data.missing.length && <tr><td colSpan={13} className="iv-empty">{t('xInventory.allClassified')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* THE GUIDE (Max 01/11: every module has one). The data-tour marks were already on the page; nothing used them until now.
   The export button is drawn only once the day has a load, and the Dashboard step needs the figures loaded, so TourGuide
   skips either when it is not there. */
const TOUR = [
  { selector: '[data-tour="iv-week"]', title: 'tourInventory.weekT', body: 'tourInventory.weekB' },
  { selector: '[data-tour="iv-tabs"]', title: 'tourInventory.tabsT', body: 'tourInventory.tabsB' },
  { selector: '[data-tour="iv-dash"]', title: 'tourInventory.dashT', body: 'tourInventory.dashB' },
  { selector: '[data-tour="iv-export"]', title: 'tourInventory.exportT', body: 'tourInventory.exportB' },
];

/* ---------------------------------------------------------------- the page */
export default function InventoryReport() {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [friday, setFriday] = useState('');
  const [newFriday, setNewFriday] = useState('');
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'dash');
  const [msg, setMsg] = useState(null);
  const flash = useCallback((text, bad) => setMsg({ text, bad }), []);
  const reload = useCallback(async () => {
    try { const s = await call('GET', '/weeks'); setState(s); setFriday((f) => f || s.weeks[0]?.friday || s.lastFriday); setNewFriday((f) => f || s.lastFriday); }
    catch (e) { flash(e.message, true); }
  }, [flash]);
  useEffect(() => { reload(); }, [reload]);
  const week = state?.weeks.find((w) => w.friday === friday);
  /* the tabs read again when a load of this day finishes (they kept the figures from before it — review 23/10) */
  const stamp = (week?.loads || []).filter((l) => l.status === 'ok').map((l) => l.load_pk).join('.');
  const running = state?.running || [];
  const anyRunning = running.length > 0 || (week?.loads || []).some((l) => l.status === 'running');
  useEffect(() => { if (!anyRunning) return undefined; const id = setInterval(reload, 4000); return () => clearInterval(id); }, [anyRunning, reload]);
  const pickTab = (k) => { setTab(k); const u = new URL(window.location.href); u.searchParams.set('tab', k); window.history.replaceState(null, '', u); };
  const openNew = () => { if (!newFriday) return; setState((s) => (s.weeks.some((w) => w.friday === newFriday) ? s : { ...s, weeks: [{ friday: newFriday, week_code: '', loads: [] }, ...s.weeks].sort((a, b) => (a.friday < b.friday ? 1 : -1)) })); setFriday(newFriday); pickTab('loads'); };
  const tabs = [['dash', t('xInventory.tab_dash')], ['loads', t('xInventory.tab_loads')], ['layers', t('xInventory.tab_layers')], ['transit', t('xInventory.tab_transit')], ['class', t('xInventory.tab_class')]];
  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main iv-page">
        <div className="mh-titlerow">
          <div>
            <h2 className="pa-title">{t('xInventory.title')}</h2>
            <p className="iv-hint">{t('xInventory.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="inventoryReport" ready={!!state && !!friday} onBeforeTour={() => pickTab('dash')} />
        </div>
        {msg && <div className={`iv-flash${msg.bad ? ' bad' : ''}`} role="status">{msg.text} <button type="button" className="pa-btn small" onClick={() => setMsg(null)}>×</button></div>}
        <div className="pa-toolbar iv-weekbar" data-tour="iv-week">
          <label className="iv-field"><span>{t('xInventory.week')}</span>
            <select value={friday} onChange={(e) => setFriday(e.target.value)}>
              {(state?.weeks || []).map((w) => <option key={w.friday} value={w.friday}>{fmtDay(w.friday)} · {weekdayOf(w.friday)}{isFriday(w.friday) && w.week_code ? ` · ${w.week_code}` : ''}{w.loads.every((l) => l.source === 'legacy') && w.loads.length ? ` · ${t('xInventory.fromWorkingFile')}` : ''}</option>)}
            </select>
          </label>
          {state?.edit && <span className="iv-newweek"><input type="date" value={newFriday} onChange={(e) => setNewFriday(e.target.value)} title={t('xInventory.newWeekHint')} /><button type="button" className="pa-btn small" onClick={openNew}>{t('xInventory.openWeek')}</button></span>}
          <span className="iv-grow" />
          <button type="button" className="pa-btn small" title={t('xInventory.refresh')} onClick={reload}><RefreshCw size={13} /></button>
          {week?.loads?.length > 0 && <a className="pa-btn small primary" data-tour="iv-export" href={`${API}/export?friday=${friday}`}><Download size={13} /> {t('xInventory.export')}</a>}
        </div>
        <div className="pa-tabs view-tabs" role="tablist" data-tour="iv-tabs">
          {tabs.map(([k, label]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={`pa-tab${tab === k ? ' active' : ''}`} onClick={() => pickTab(k)}><TabIcon k={k} />{label}</button>)}
        </div>
        {!state || !friday ? <p className="iv-hint">{t('xInventory.loading')}</p> : (
          <>
            {tab === 'dash' && <Dashboard key={`${friday}|${stamp}`} t={t} friday={friday} flash={flash} />}
            {tab === 'loads' && <Loads t={t} week={week} friday={friday} edit={state.edit} running={running} reload={reload} flash={flash} />}
            {tab === 'layers' && <Layers key={stamp} t={t} friday={friday} flash={flash} />}
            {tab === 'transit' && <Transit key={stamp} t={t} friday={friday} flash={flash} />}
            {tab === 'class' && <Classification t={t} friday={friday} edit={state.edit} flash={flash} />}
          </>
        )}
      </div>
    </div>
  );
}
